import { useState, useCallback, useMemo } from 'react';
import { useAccessToken } from '../../../hooks/useAccessToken';
import { useLogger } from '../../../context/useLogger';
import type { AerBilanData, AerBilanResponse, DownloadResponse } from '../../../types/bilan.types';
import { saveAs } from 'file-saver';
import { EdiDsnBilansFileService } from '../../../services/EdiDsnBilansFileService';

export const useAerBilan = () => {
  const getAccessToken = useAccessToken();
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AerBilanData | null>(null);
  const logger = useLogger();

  const ediBilanFileService = useMemo(
    () => new EdiDsnBilansFileService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const fetchAerBilan = useCallback(
    async (instanceId: string): Promise<AerBilanResponse> => {
      if (!instanceId) {
        setError('Instance ID is required');
        return { data: null, success: false, error: 'Instance ID is required' };
      }

      setLoading(true);
      setError(null);
      setData(null);

      try {
        const bilanResult = await ediBilanFileService.tryFetchDsnBilansFileAerContent([instanceId]);

        if (!bilanResult.success || !bilanResult.data) {
          const errorMessage =
            (bilanResult.success ? null : bilanResult.error) ?? 'Failed to fetch AER Bilan content';

          logger.error('[Fetch] ❌ Error AER Bilan fetch', errorMessage);
          setError(errorMessage);
          setLoading(false);
          return { data: null, success: false, error: errorMessage };
        }

        logger.info('[fetchAerBilan] Download AER Bilan ContentType: ', bilanResult.contentType);
        if (
          bilanResult.filename?.toLowerCase().endsWith('.pdf') ||
          bilanResult.contentType?.split(';')[0].trim().toLowerCase() === 'application/pdf'
        ) {
          const pdfBlob = new Blob([bilanResult.data], { type: 'application/pdf' });
          const pdfUrl = URL.createObjectURL(pdfBlob);

          setData({ pdfUrl });
          setLoading(false);

          return { data: { pdfUrl }, success: true };
        }

        saveAs(bilanResult.data, bilanResult.filename || 'download-bilan.zip');
        setLoading(false);

        return {
          data: { content: undefined },
          success: true,
        };
      } catch (ex) {
        const errorMessage = ex instanceof Error ? ex.message : 'An unknown error occurred';
        logger.error('[Fetch] ❌ Error AER Bilan fetch', ex);
        setError(errorMessage);
        setLoading(false);

        return { data: null, success: false, error: errorMessage };
      }
    },
    [ediBilanFileService, logger],
  );

  const downloadMultipleAerBilans = useCallback(
    async (instanceIds: string[]): Promise<DownloadResponse> => {
      if (!instanceIds.length) {
        setError('Aucune déclaration sélectionnée');
        return { success: false, error: 'Aucune déclaration sélectionnée' };
      }

      setDownloadLoading(true);
      setError(null);

      try {
        const bilansResult = await ediBilanFileService.tryFetchDsnBilansFileAerContent(instanceIds);

        if (!bilansResult.success || !bilansResult.data) {
          const errorMessage =
            (bilansResult.success ? null : bilansResult.error) ??
            'Failed to download AER Bilans content';

          logger.error('[Download] ❌ Error AER Bilans download', errorMessage);
          setError(errorMessage);
          setDownloadLoading(false);
          return { success: false, error: errorMessage };
        }

        logger.info(
          '[downloadMultipleAerBilans] Download AER Bilans ContentType: ',
          bilansResult.contentType,
        );
        // A single declaration can also return an archive containing several bilans.
        const fallbackFilename =
          bilansResult.contentType?.split(';')[0].trim().toLowerCase() === 'application/pdf'
            ? `declaration-${instanceIds[0]}.pdf`
            : `declarations-${new Date().toISOString().slice(0, 10)}.zip`;

        saveAs(bilansResult.data, bilansResult.filename || fallbackFilename);
        setDownloadLoading(false);

        return { success: true };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Une erreur est survenue lors du téléchargement';
        logger.error('[Download] ❌ Error AER Bilan download', err);
        setError(errorMessage);
        setDownloadLoading(false);
        return { success: false, error: errorMessage };
      }
    },
    [ediBilanFileService, logger],
  );

  return {
    fetchAerBilan,
    downloadMultipleAerBilans,
    loading,
    downloadLoading,
    error,
    data,
  };
};
